import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DecimalPipe } from '@angular/common';

import { SiteService } from '../../../core/services/site.service';
import { AuthService } from '../../../core/auth/auth.service';
import { SiteManagementDepartmentService } from '../../../core/services/site-management-department.service';
import { LoadingService } from '../../../core/services/loading.service';
import { NotificationService } from '../../../core/services/notification.service';
import { SensorService } from '../../../core/services/sensor.service';
import { getNoiseStatusBadgeClass, getWbgtStatusBadgeClass, getWindStatusBadgeClass } from '../../utils/sensor-status';
import { 
  mapWbgtSeries, 
  mapWindSeries, 
  mapNoiseSeries, 
  mapVibrationSeries, 
  mapPhSeries, 
  mapTemperatureSeries,
  mapHumiditySeries
} from '../../charts/utils/sensor-history-mapper';
import type { ChartDataPoint, ChartPeriod, ChartSeries } from '../../charts/interfaces/chart-data.interface';

import { WbgtLineChart } from '../../charts/wbgt-line-chart/wbgt-line-chart';
import { WindLineChart } from '../../charts/wind-line-chart/wind-line-chart';
import { NoiseLineChart } from '../../charts/noise-line-chart/noise-line-chart';
import { VibrationLineChart } from '../../charts/vibration-line-chart/vibration-line-chart';
import { PhLineChart } from '../../charts/ph-line-chart/ph-line-chart';
import { TemperatureLineChart } from '../../charts/temperature-line-chart/temperature-line-chart';
import { HumidityLineChart } from '../../charts/humidity-line-chart/humidity-line-chart';
import { getDefaultSeasonMode } from '../../utils/season-mode';
import { FeatureToggleDirective } from '../../directives/feature-toggle.directive';
import { WnDialogComponent } from '../wn-dialog/wn-dialog';
import { SiteAlertBannerComponent } from '../site-alert-banner/site-alert-banner';
import { WindCompassComponent } from '../wind-compass/wind-compass';

interface LatestValueView {
  label: string;
  value?: number;
  unit: string;
  date?: string;
  statusLabel?: string;
  badgeClass?: string;
  extra?: ChartDataPoint['extra'];
}

function latestValueFromSeries(series: ChartSeries[], label: string, unit: string): LatestValueView {
  const latestPoint = series.reduce<ChartDataPoint | undefined>((latest, item) => {
    const current = item.data[0];
    if (!current) return latest;
    if (!latest || current.timestamp > latest.timestamp) return current;
    return latest;
  }, undefined);

  return {
    label,
    value: latestPoint?.value,
    unit,
    extra: latestPoint?.extra,
  };
}

@Component({
  selector: 'app-site-dashboard',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatButtonModule,
    MatButtonToggleModule,
    DecimalPipe,
    WbgtLineChart,
    WindLineChart,
    NoiseLineChart,
    VibrationLineChart,
    PhLineChart,
    TemperatureLineChart,
    HumidityLineChart,
    FeatureToggleDirective,
    SiteAlertBannerComponent,
    WindCompassComponent,
    MatMenuModule,
    MatDialogModule
  ],
  templateUrl: './site-dashboard.html',
  styleUrl: './site-dashboard.scss',
})
export class SiteDashboardComponent {
  private siteService = inject(SiteService);
  private authService = inject(AuthService);
  private siteManagementDepartmentService = inject(SiteManagementDepartmentService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);
  private sensorService = inject(SensorService);
  private dialog = inject(MatDialog);

  public siteId = input<string | undefined>(undefined);
  public showHeader = input<boolean>(true);
  public containerClass = input<string>('p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6');

  public readonly period = this.sensorService.chartPeriod;
  public readonly seasonMode = signal<'summer' | 'winter'>(getDefaultSeasonMode());

  public onPeriodChange(p: ChartPeriod): void {
    this.sensorService.chartPeriod.set(p);
  }

  public openWeatherNews(mode: 'today' | 'tomorrow' | 'weekly'): void {
    const site = this.rawSiteDetail();
    if (site?.geopoint) {
      this.dialog.open(WnDialogComponent, {
        data: { lat: site.geopoint.latitude, lon: site.geopoint.longitude, mode },
        width: '90vw',
        maxWidth: '800px',
        maxHeight: '90vh'
      });
    } else {
      this.notificationService.error('現場の位置情報が取得できませんでした');
    }
  }

  private readonly wbgtSensors = computed(() => this.sensorService.sensorsByCategory().wbgt);
  private readonly histories = computed(() => this.sensorService.histories());

  // --- WBGTセンサーごとにグループ化（WBGT/温度/湿度を縦並び） ---
  // センサーが設定されていればデータなしでも空グラフを表示
  public readonly wbgtSensorGroups = computed(() =>
    this.wbgtSensors().map(sensor => {
      const status = this.siteDetail()?.wbgtSensors?.find(item => item.id === sensor.id);
      return {
        sensorId: sensor.id,
        sensorType: 'WBGT' as const,
        positionName: sensor.positionName,
        wbgt: mapWbgtSeries([sensor], this.histories()),
        temperature: mapTemperatureSeries([sensor], this.histories()),
        humidity: mapHumiditySeries([sensor], this.histories()),
        latest: {
          label: 'WBGT',
          value: status?.wbgt,
          unit: '℃',
          date: status?.date,
          statusLabel: status?.statusLabel,
          badgeClass: status?.badgeClass,
        } satisfies LatestValueView,
      };
    })
  );

  public readonly windSensorGroups = computed(() =>
    this.sensorService.sensorsByCategory().wind.map(sensor => {
      const wind = mapWindSeries([sensor], this.histories());
      const latest = latestValueFromSeries(wind, '風速', 'm/s');
      const detail = this.siteDetail();
      const windDirection = latest.extra?.['windDirection'];
      const windDirectionCode = latest.extra?.['windDirectionCode'];

      return {
        sensorId: sensor.id,
        sensorType: 'WIND' as const,
        positionName: sensor.positionName,
        wind,
        latest: {
          ...latest,
          date: detail?.windDate,
          statusLabel: detail?.windSpeedStatusLabel,
          badgeClass: detail?.windSpeedBadgeClass,
        } satisfies LatestValueView,
        windDirection: typeof windDirection === 'string' ? windDirection : undefined,
        windDirectionCode: typeof windDirectionCode === 'number' ? windDirectionCode : undefined,
        northAngle: detail?.northAngle ?? 0,
      };
    })
  );

  /** 風速: 後方互換用 */
  public readonly windCharts = computed(() => this.windSensorGroups().map(group => group.wind));

  public readonly noiseSensorGroups = computed(() =>
    this.sensorService.sensorsByCategory().noise.map(sensor => {
      const noise = mapNoiseSeries([sensor], this.histories());
      const vibration = mapVibrationSeries([sensor], this.histories());
      const status = this.siteDetail()?.noiseSensors?.find(item => item.id === sensor.id);

      return {
        sensorId: sensor.id,
        sensorType: 'NOISE' as const,
        positionName: sensor.positionName,
        noise,
        vibration,
        latestNoise: latestValueFromSeries(noise, '騒音', 'dB'),
        latestVibration: latestValueFromSeries(vibration, '振動', 'dB'),
        status,
      };
    })
  );

  public readonly phSensorGroups = computed(() =>
    this.sensorService.sensorsByCategory().ph.map(sensor => {
      const ph = mapPhSeries([sensor], this.histories());

      return {
        sensorId: sensor.id,
        sensorType: 'PH' as const,
        positionName: sensor.positionName,
        ph,
        latest: latestValueFromSeries(ph, 'pH', ''),
      };
    })
  );

  // --- カテゴリ単位の後方互換プロパティ ---
  public readonly noiseSeries = computed(() => this.noiseSensorGroups().flatMap(group => group.noise));
  public readonly vibrationSeries = computed(() => this.noiseSensorGroups().flatMap(group => group.vibration));
  public readonly phSeries = computed(() => this.phSensorGroups().flatMap(group => group.ph));

  public readonly wbgtSeries = computed(() => mapWbgtSeries(this.wbgtSensors(), this.histories()));
  public readonly temperatureSeries = computed(() => mapTemperatureSeries(this.wbgtSensors(), this.histories()));
  public readonly humiditySeries = computed(() => mapHumiditySeries(this.wbgtSensors(), this.histories()));
  public readonly windSeries = computed(() => mapWindSeries(this.sensorService.sensorsByCategory().wind, this.histories()));
  public readonly latestSiteData = computed(() => this.siteService.getLatestSiteData(this.rawSiteDetail()?.id ?? null));
  public readonly isOuterUser = computed(() => this.authService.userRole() === 'viewer');

  // IDが指定されていない場合は、SiteServiceの一覧から最初の現場を取得する
  private rawSiteDetail = computed(() => {
    const id = this.siteId();
    if (id) {
      return this.siteService.getSiteDetail(id);
    }
    // IDがない場合は最初の現場を返す
    const list = this.siteService.siteListItems();
    if (list.length > 0) {
      return this.siteService.getSiteDetail(list[0].id);
    }
    return undefined;
  });

  public siteDetail = computed(() => {
    const detail = this.rawSiteDetail();
    if (!detail) return undefined;

    const depts = this.siteManagementDepartmentService.departments();
    const deptName = depts.find(d => d.id === detail.managementDepartmentId)?.name || '未設定';

    // 各WBGTセンサーにバッジクラスを算出して付与
    const wbgtSensors = detail.wbgtSensors?.map(s => ({
      ...s,
      badgeClass: getWbgtStatusBadgeClass(s.statusNo),
    }));
    const noiseSensors = detail.noiseSensors?.map(s => ({
      ...s,
      badgeClass: getNoiseStatusBadgeClass(s.statusNo),
    }));

    return {
      ...detail,
      managementDepartmentName: deptName,
      wbgtSensors,
      noiseSensors,
      wbgtBadgeClass: getWbgtStatusBadgeClass(detail.wbgtStatusNo),
      wbgtStatusLabel: detail.wbgtStatusLabel || '不明',
      windSpeedBadgeClass: getWindStatusBadgeClass(detail.windSpeedStatusNo),
      windSpeedStatusLabel: detail.windSpeedStatusLabel || '不明',
      noiseBadgeClass: getNoiseStatusBadgeClass(detail.noiseStatusNo),
      noiseStatusLabel: detail.noiseStatusLabel || '不明'
    };
  });

  constructor() {
    let wasLoading = false;
    effect(() => {
      const isLoading = this.siteService.isLoading() || this.siteManagementDepartmentService.isLoading() || this.sensorService.isLoading();
      if (isLoading && !wasLoading) {
        this.loadingService.show();
        wasLoading = true;
      } else if (!isLoading && wasLoading) {
        this.loadingService.hide();
        wasLoading = false;
      }
    });

    effect(() => {
      const siteError = this.siteService.error();
      if (siteError) {
        this.notificationService.error(siteError);
      }
    });

    effect(() => {
      const deptError = this.siteManagementDepartmentService.error();
      if (deptError) {
        this.notificationService.error(deptError);
      }
    });

    effect(() => {
      const detail = this.rawSiteDetail();
      this.sensorService.siteId.set(detail?.id || null);
    });

    effect(() => {
      const sensorError = this.sensorService.error();
      if (sensorError) {
        this.notificationService.error(sensorError);
      }
    });
  }
}
